/**
 * Step1Policy Component
 *
 * Collects:
 * - Carrier name (dropdown with 10 carriers + "Other" option)
 * - If "Other" selected: carrier name text input
 * - Policy number (text input)
 *
 * Uses react-hook-form's useFormContext and Controller for managed state.
 * Validates against step1Schema from formSchemas.
 */

import { useFormContext, Controller } from 'react-hook-form';
import { Emery } from '@/components/ui/Calla';
import { EMERY_MESSAGES } from '@/constants/callaMessages';
import { FormField } from '@/components/ui/FormField';
import { AnimatedReveal } from '@/components/ui/AnimatedReveal';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { CARRIER_OPTIONS, type FullFormData } from '@/schemas/formSchemas';

export default function Step1Policy() {
  const { control, formState: { errors }, watch } = useFormContext<FullFormData>();
  const carrierName = watch('step1.carrierName');
  const showCarrierOther = carrierName === 'Other';

  return (
    <div>
      <Emery message={EMERY_MESSAGES.step1} />

      {/* Carrier Name Select */}
      <FormField
        label="Insurance Carrier"
        name="step1.carrierName"
        required={true}
        error={errors.step1?.carrierName?.message}
        whyText="We need your current carrier to pull your policy details and begin negotiations."
      >
        <Controller
          name="step1.carrierName"
          control={control}
          render={({ field }) => (
            <SearchableSelect
              options={CARRIER_OPTIONS.map((c) => ({ value: c, label: c }))}
              value={field.value}
              onChange={field.onChange}
              placeholder="Search or select your carrier"
              error={!!errors.step1?.carrierName}
            />
          )}
        />
      </FormField>

      {/* Conditional: Other Carrier Name Input */}
      <AnimatedReveal isVisible={showCarrierOther}>
        <FormField
          label="Please specify your carrier"
          name="step1.carrierNameOther"
          required={true}
          error={errors.step1?.carrierNameOther?.message}
        >
          <Controller
            name="step1.carrierNameOther"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                autoComplete="organization"
                autoCapitalize="words"
                enterKeyHint="next"
                placeholder="e.g., Hartford, AIG, etc."
                className={`form-input ${
                  errors.step1?.carrierNameOther ? 'form-input-error' : ''
                }`}
              />
            )}
          />
        </FormField>
      </AnimatedReveal>

      {/* Policy Number Input */}
      <FormField
        label="Policy Number"
        name="step1.policyNumber"
        required={true}
        sensitive={true}
        error={errors.step1?.policyNumber?.message}
        helperText="This is on your insurance card or recent policy statement"
        whyText="Your policy number allows us to verify your coverage and pull your current rates."
      >
        <Controller
          name="step1.policyNumber"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              enterKeyHint="done"
              data-sensitive=""
              placeholder="e.g., ABC123456"
              className={`form-input ${
                errors.step1?.policyNumber ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>
    </div>
  );
}
